import classNames from 'classnames';
import { m, useReducedMotion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { default as BarChartStartAsset } from '@/assets/graphics/barChartStart.svg';
import styles from './bar-chart.module.scss';

export type BarChartColors = 'red' | 'black' | 'blue';

const ASSET_WIDTH = 41;

export interface BarChartProps {
  /** The class name to apply */
  className?: string;
  /** The percentage width of the bar */
  value?: number;
  /** Background color variant for the bar */
  variant: BarChartColors;
}

const variants: Record<BarChartColors, string> = {
  black: styles.isBlack,
  blue: styles.isBlue,
  red: styles.isRed,
};

const InnerContainer = ({
  value,
  inView = false,
}: {
  inView: boolean;
  value: number;
}) => {
  const shouldReduceMotion = useReducedMotion();

  return (
    <m.div
      animate={{
        width: shouldReduceMotion ? `${value}%` : inView ? `${value}%` : '0%',
      }}
      className={styles.bar}
      initial={{ width: '0%' }}
      transition={shouldReduceMotion ? { duration: 0 } : { duration: 1 }}
    />
  );
};

const BarChart = ({ className, value = 100, variant }: BarChartProps) => {
  const [inView, setInView] = useState(false);
  const barRef = useRef<HTMLDivElement | null>(null);
  const shouldReduceMotion = useReducedMotion();
  const [assetCount, setAssetCount] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );

    if (barRef.current) {
      observer.observe(barRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (barRef.current) {
      const barWidth = barRef.current.offsetWidth;
      const maxAssetCount = Math.floor(
        (barWidth * (value / 100)) / ASSET_WIDTH,
      );

      setAssetCount(Math.max(maxAssetCount, 1));
    }
  }, [value]);

  return (
    <div
      ref={barRef}
      className={classNames(styles.component, variants[variant], className)}
    >
      <m.div
        animate={{
          clipPath: inView
            ? `inset(0 ${100 - value}% 0 0)`
            : 'inset(0 100% 0 0)',
        }}
        className={styles.barClipPath}
        initial={{ clipPath: 'inset(0 100% 0 0)' }}
        transition={shouldReduceMotion ? { duration: 0 } : { duration: 1 }}
      >
        {Array.from({ length: assetCount }).map((_, index) => (
          <BarChartStartAsset key={index} className={styles.barChartStart} />
        ))}

        <InnerContainer inView={inView} value={value} />
      </m.div>
    </div>
  );
};

export default BarChart;
